import { useRouter } from 'vue-router';
import qs from 'qs';

/**
 * Composable for syncing state with browser URL search params using the qs library.
 * Supports nested objects and arrays in query strings.
 *
 * @param {Object} [options] - Optional configuration
 * @param {Object} [options.parseOptions] - Options passed to qs.parse (e.g. arrayLimit, depth)
 * @param {Object} [options.stringifyOptions] - Options passed to qs.stringify (e.g. arrayFormat: 'brackets', encode: false)
 *
 * @returns {Object} Object with params (object from URL) and setParams (function to update URL from object)
 *
 * @example
 * // Read current search params as object
 * const { params, setParams } = useSearchParams();
 * console.log(params); // { search_text: 'test', search_resource: 'statements' }
 *
 * @example
 * // Set search params from object (replaces current query)
 * setParams({ search_text: 'test', search_resource: 'statements' });
 */

// Keeps commas unescaped (a legal URI char) so qs's `comma: true` parse option can split
// arrays back out. Opt-in only, since it would corrupt free-text params containing commas.
export function encodeValuePreservingCommas(value, defaultEncoder, charset, type) {
  const encoded = defaultEncoder(value, defaultEncoder, charset, type);
  return type === 'value' ? encoded.replace(/%2C/g, ',') : encoded;
}

export function useSearchParams(options = {}) {
  const router = useRouter();

  const {
    parseOptions = { ignoreQueryPrefix: true, allowDots: true },
    stringifyOptions = { skipNulls: true, allowDots: true },
  } = options;

  const params = qs.parse(location.search, parseOptions);

  /**
   * Updates the browser URL search params from a plain object.
   * Uses router.replace so the current route stays the same and back/forward work correctly.
   *
   * @param {Object} obj - Plain object to serialize to query string (nested objects and arrays supported)
   */
  function setParams(newParams) {
    const mergedParams = { ...params, ...newParams };
    const search = qs.stringify(mergedParams, stringifyOptions);
    const newPath = location.pathname + (search ? `?${search}` : '');
    router.replace(newPath);
  }

  function clearParams() {
    router.replace(location.pathname);
  }

  return {
    params,
    setParams,
    clearParams,
  };
}
